import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from '../utils/toast';
import { logClientError } from '../utils/errorLog';

// Base URL configuration - use relative URL so it uses the same protocol as the page
const BASE_URL = process.env.REACT_APP_API_URL || '';

const cookieOptions = (days) => ({
  expires: days,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict'
});

// Create axios instance
const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Bare instance that bypasses interceptors (used for refresh calls to avoid loops)
const bareApi = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Refresh queue to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');
    // A failed passkey sign-in is a 401 with its own message. Don't treat it as
    // an expired session and swap in a refresh error.
    const isPasskeyLogin = originalRequest.url?.includes('/auth/passkey/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest && !isPasskeyLogin) {
      // Check if another tab already refreshed the token
      const currentToken = Cookies.get('accessToken');
      const requestToken = originalRequest.headers.Authorization?.split(' ')[1];
      if (currentToken && currentToken !== requestToken) {
        originalRequest.headers.Authorization = `Bearer ${currentToken}`;
        return api(originalRequest);
      }

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = Cookies.get('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await bareApi.post('/auth/refresh', { refreshToken });
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        const isTabletsHost = window.location.hostname.toLowerCase().startsWith('tablets.');
        Cookies.set('accessToken', accessToken, cookieOptions(1));
        Cookies.set('refreshToken', newRefreshToken, cookieOptions(isTabletsHost ? 90 : 30));

        processQueue(null, accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        Cookies.remove('accessToken');
        Cookies.remove('refreshToken');
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Report the failure so admins can review what users are hitting.
    // 401s are skipped — they're normal session-refresh traffic, not errors.
    const status = error.response?.status;
    const reqUrl = originalRequest?.url || '';
    if (status !== 401) {
      logClientError({
        source: 'api',
        message: error.response?.data?.message || error.message || 'Request failed',
        method: originalRequest?.method?.toUpperCase(),
        url: reqUrl,
        status,
        // Blob error bodies (failed downloads) stringify to '{}' — skip them.
        detail: error.response?.data && !(error.response.data instanceof Blob)
          ? JSON.stringify(error.response.data)
          : undefined
      });
    }

    // Map/route/mileage lookups are background niceties on the driver tablet
    // pages — when the routing service hiccups, don't alarm users with a
    // generic "Server error" toast. The failure is still logged above, and
    // each caller degrades gracefully (badge hides, modal shows its own note).
    const isRoutingRequest =
      /\/driver-tablets\/(mileage|route|tracking)/.test(reqUrl) ||
      /\/driver-tablets\/trips\/[^/]+\/(route|recompute-miles)/.test(reqUrl) ||
      /\/driver-tablets\/drivers\/[^/]+\/location/.test(reqUrl);

    // Handle other errors
    if (!isRoutingRequest) {
      if (status >= 500) {
        toast.error('Server error. Please try again later.');
      } else if (status === 403) {
        toast.error('Access denied');
      } else if (status === 404) {
        toast.error('Resource not found');
      }
    }

    return Promise.reject(error);
  }
);

// Auth API endpoints
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  verify2FA: (data) => api.post('/auth/verify-2fa', data),
  resend2FA: (data) => api.post('/auth/resend-2fa', data),
  logout: () => api.post('/auth/logout'),
  refresh: (data) => api.post('/auth/refresh', data),
  silentRefresh: (data) => bareApi.post('/auth/refresh', data),
  verify: () => api.get('/auth/verify'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  passkeyRegisterOptions: () => api.post('/auth/passkey/register/options'),
  passkeyRegisterVerify: (data) => api.post('/auth/passkey/register/verify', data),
  passkeyLoginOptions: () => api.post('/auth/passkey/login/options'),
  passkeyLoginVerify: (data) => api.post('/auth/passkey/login/verify', data),
  getPasskeys: () => api.get('/auth/passkeys'),
  getAllPasskeys: () => api.get('/auth/passkeys/all'),
  getUserPasskeys: (userId) => api.get(`/auth/passkeys/user/${userId}`),
  renamePasskey: (id, data) => api.patch(`/auth/passkeys/${id}`, data),
  deletePasskey: (id) => api.delete(`/auth/passkeys/${id}`),
};

// User API endpoints
export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (userId) => api.get(`/users/${userId}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (userId, data) => api.put(`/users/${userId}`, data),
  updateProfile: (data) => api.put('/users/profile/me', data),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  resetPassword: (userId, data) => api.post(`/users/${userId}/reset-password`, data),
  unlockUser: (userId) => api.post(`/users/${userId}/unlock`),
  getLoginEvents: (userId, params) => api.get(`/users/${userId}/login-events`, { params }),
  getUserSessions: (userId) => api.get(`/users/${userId}/sessions`),
  invalidateSession: (userId, sessionId) => api.delete(`/users/${userId}/sessions/${sessionId}`),
  
  // Custom tag filters
  getCustomTagFilters: () => api.get('/users/profile/tag-filters'),
  addCustomTagFilter: (data) => api.post('/users/profile/tag-filters', data),
  updateCustomTagFilter: (originalLabel, data) => api.put(`/users/profile/tag-filters/${encodeURIComponent(originalLabel)}`, data),
  removeCustomTagFilter: (label) => api.delete(`/users/profile/tag-filters/${encodeURIComponent(label)}`),
  
  // Admin statistics
  getAdminStatistics: () => api.get('/users/admin/statistics'),
  getActivityLogs: (params) => api.get('/users/admin/activity', { params }),
  getLoginHistory: (userId, params) => api.get(`/users/${userId}/login-history`, { params }),
};

// Backup API endpoints (admin)
export const backupAPI = {
  getStats: () => api.get('/backups/stats'),
};

// Page API endpoints
export const pageAPI = {
  getPages: (params) => api.get('/pages', { params }),
  getPage: (pageId) => api.get(`/pages/${pageId}`),
  getPageContent: async (pageId) => {
    // First get page info to check content type
    const pageResponse = await api.get(`/pages/${pageId}`);
    const page = pageResponse.data.page;
    
    if (page.contentType === 'link') {
      // For links, get JSON response
      return api.get(`/pages/${pageId}/content`);
    } else {
      // For files, get blob response  
      return api.get(`/pages/${pageId}/content`, {
        responseType: 'blob'
      });
    }
  },
  createPage: (formData) => {
    return api.post('/pages', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updatePage: (pageId, data) => {
    const isFormData = data instanceof FormData;
    return api.put(`/pages/${pageId}`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
    });
  },
  deletePage: (pageId) => api.delete(`/pages/${pageId}`),
  // Version history endpoints
  getPageVersions: (pageId) => api.get(`/pages/${pageId}/versions`),
  getPageVersionContent: (pageId, versionNumber, contentType) =>
    api.get(`/pages/${pageId}/versions/${versionNumber}/content`,
      contentType === 'link' ? {} : { responseType: 'blob' }
    ),
  restorePageVersion: (pageId, versionNumber) => api.post(`/pages/${pageId}/versions/${versionNumber}/restore`),
  getCategories: () => api.get('/pages/meta/categories'),
  getStats: () => api.get('/pages/meta/stats'),
  // Favorite endpoints
  addToFavorites: (pageId) => api.post(`/pages/${pageId}/favorite`),
  removeFromFavorites: (pageId) => api.delete(`/pages/${pageId}/favorite`),
  getFavorites: () => api.get('/pages/meta/favorites'),
};

// Incident API endpoints
export const incidentAPI = {
  // Incident management
  getIncidents: (params) => api.get('/incidents', { params }),
  getIncident: (incidentId) => api.get(`/incidents/${incidentId}`),
  createIncident: (data) => api.post('/incidents', data),
  updateIncident: (incidentId, data) => api.put(`/incidents/${incidentId}`, data),
  getCategories: () => api.get('/incidents/utils/categories'),
  getIncidentsByEmployee: (employeeId) => api.get(`/incidents/by-employee/${employeeId}`),

  // Document management
  addDocument: (incidentId, formData) => {
    return api.post(`/incidents/${incidentId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateDocument: (incidentId, documentId, data) => api.put(`/incidents/${incidentId}/documents/${documentId}`, data),
  deleteDocument: (incidentId, documentId) => api.delete(`/incidents/${incidentId}/documents/${documentId}`),
  downloadDocument: (incidentId, documentId) => api.get(`/incidents/${incidentId}/documents/${documentId}/download`, {
    responseType: 'blob'
  }),

  // Note management
  addNote: (incidentId, data) => api.post(`/incidents/${incidentId}/notes`, data),
  updateNote: (incidentId, noteId, data) => api.put(`/incidents/${incidentId}/notes/${noteId}`, data),
  deleteNote: (incidentId, noteId) => api.delete(`/incidents/${incidentId}/notes/${noteId}`),
};

// Password API endpoints
export const passwordAPI = {
  // Password management
  getPasswords: (params) => api.get('/passwords', { params }),
  getPassword: (passwordId) => api.get(`/passwords/${passwordId}`),
  createPassword: (data) => api.post('/passwords', data),
  updatePassword: (passwordId, data) => api.put(`/passwords/${passwordId}`, data),
  deletePassword: (passwordId) => api.delete(`/passwords/${passwordId}`),
  getCategories: () => api.get('/passwords/utils/categories'),
  getStats: () => api.get('/passwords/utils/stats'),

  // Document management
  addDocument: (passwordId, formData) => {
    return api.post(`/passwords/${passwordId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateDocument: (passwordId, documentId, data) => api.put(`/passwords/${passwordId}/documents/${documentId}`, data),
  deleteDocument: (passwordId, documentId) => api.delete(`/passwords/${passwordId}/documents/${documentId}`),
  downloadDocument: (passwordId, documentId) => api.get(`/passwords/${passwordId}/documents/${documentId}/download`, {
    responseType: 'blob'
  }),

  // Note management
  addNote: (passwordId, data) => api.post(`/passwords/${passwordId}/notes`, data),
  updateNote: (passwordId, noteId, data) => api.put(`/passwords/${passwordId}/notes/${noteId}`, data),
  deleteNote: (passwordId, noteId) => api.delete(`/passwords/${passwordId}/notes/${noteId}`),

  // Revision management
  getRevisions: (passwordId, params = {}) => api.get(`/passwords/${passwordId}/revisions`, { params }),
  getRevision: (passwordId, revisionId) => api.get(`/passwords/${passwordId}/revisions/${revisionId}`),
  getRevisionPassword: (passwordId, revisionId) => api.get(`/passwords/${passwordId}/revisions/${revisionId}/password`),

  // Favicon management
  refreshFavicon: (passwordId) => api.post(`/passwords/${passwordId}/refresh-favicon`),
  previewFavicon: (url) => api.post('/passwords/preview-favicon', { url }, { timeout: 10000 }), // 10 second timeout for favicon
};

// Note API endpoints
export const noteAPI = {
  // Note management
  getNotes: (params) => api.get('/notes', { params }),
  getNote: (noteId) => api.get(`/notes/${noteId}`),
  createNote: (data) => api.post('/notes', data),
  updateNote: (noteId, data) => api.put(`/notes/${noteId}`, data),
  deleteNote: (noteId) => api.delete(`/notes/${noteId}`),
  getCategories: () => api.get('/notes/utils/categories'),

  // Revision management
  getRevisions: (noteId, params = {}) => api.get(`/notes/${noteId}/revisions`, { params }),
  getRevision: (noteId, revisionId) => api.get(`/notes/${noteId}/revisions/${revisionId}`),
};

// Employee API endpoints
export const employeeAPI = {
  // Employee management
  getEmployees: (params) => api.get('/employees', { params }),
  getEmployee: (employeeId) => api.get(`/employees/${employeeId}`),
  createEmployee: (data) => api.post('/employees', data),
  updateEmployee: (employeeId, data) => api.put(`/employees/${employeeId}`, data),
  deleteEmployee: (employeeId) => api.delete(`/employees/${employeeId}`),

  // Document management
  getAllDocuments: () => api.get('/employees/documents/all'),
  addDocument: (employeeId, formData) => {
    return api.post(`/employees/${employeeId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateDocument: (employeeId, documentId, data) => api.put(`/employees/${employeeId}/documents/${documentId}`, data),
  deleteDocument: (employeeId, documentId) => api.delete(`/employees/${employeeId}/documents/${documentId}`),
  downloadDocument: (employeeId, documentId) => api.get(`/employees/${employeeId}/documents/${documentId}/download`, {
    responseType: 'blob'
  }),

  // Vacation management
  getVacationSummary: () => api.get('/employees/vacation/summary'),
  addVacation: (employeeId, data) => api.post(`/employees/${employeeId}/vacation`, data),
  updateVacation: (employeeId, vacationId, data) => api.put(`/employees/${employeeId}/vacation/${vacationId}`, data),
  deleteVacation: (employeeId, vacationId) => api.delete(`/employees/${employeeId}/vacation/${vacationId}`),
};

// Transaction API endpoints
export const transactionAPI = {
  // Transaction management
  getTransactions: (params) => api.get('/transactions', { params }),
  getTransaction: (transactionId) => api.get(`/transactions/${transactionId}`),
  createTransaction: (data) => api.post('/transactions', data),
  updateTransaction: (transactionId, data) => api.put(`/transactions/${transactionId}`, data),
  deleteTransaction: (transactionId) => api.delete(`/transactions/${transactionId}`),
  markQuickbooks: (transactionId, enteredInQuickbooks) => api.patch(`/transactions/${transactionId}/quickbooks`, { enteredInQuickbooks }),
};

// Invoice API endpoints
export const invoiceAPI = {
  // Invoice management
  getInvoices: (params) => api.get('/invoices', { params }),
  getInvoice: (invoiceId) => api.get(`/invoices/${invoiceId}`),
  createInvoice: (data) => api.post('/invoices', data),
  updateInvoice: (invoiceId, data) => api.put(`/invoices/${invoiceId}`, data),
  deleteInvoice: (invoiceId) => api.delete(`/invoices/${invoiceId}`),
  markQuickbooks: (invoiceId, enteredInQuickbooks) => api.patch(`/invoices/${invoiceId}/quickbooks`, { enteredInQuickbooks }),
};

// QuickBooks API endpoints
export const quickbooksAPI = {
  // Invoice management
  getInvoices: (params) => api.get('/quickbooks/invoices', { params }),
  getInvoice: (invoiceId) => api.get(`/quickbooks/invoices/${invoiceId}`),
  getInvoicesByWeek: (weekKey) => api.get(`/quickbooks/invoices-by-week/${weekKey}`),
  // Customer management
  getCustomers: (params) => api.get('/quickbooks/customers', { params }),
  getCustomersYTD: (params) => api.get('/quickbooks/customers-ytd', { params }),
  getAvailableSalesYears: () => api.get('/quickbooks/customers-ytd', { params: { limit: 1 } }), // Just to get availableYears
  getCustomer: (customerId) => api.get(`/quickbooks/customers/${customerId}`),
  getCustomerPayments: (customerId, params) => api.get(`/quickbooks/customers/${customerId}/payments`, { params }),
  getCustomerDaysToPay: (customerId) => api.get(`/quickbooks/customers/${customerId}/days-to-pay`),
  getCustomerCreditMemos: (customerId) => api.get(`/quickbooks/customers/${customerId}/credit-memos`),
  toggleCustomerExclude: (customerId, reason) => api.post(`/quickbooks/customers/${customerId}/toggle-exclude`, { reason }),
  // Payments
  getPayments: (params) => api.get('/quickbooks/payments', { params }),
  // Credit Memos
  getCreditMemos: (params) => api.get('/quickbooks/credit-memos', { params }),
  // Statistics & Reports
  getStats: () => api.get('/quickbooks/stats'),
  getAgingReport: (includeExcluded = false) => api.get('/quickbooks/aging-report', { params: { includeExcluded } }),
  getAgingPaid: (includeExcluded = false) => api.get('/quickbooks/aging-paid', { params: { includeExcluded } }),
  getWeeklyInvoicedStats: (weeks) => api.get('/quickbooks/weekly-invoiced-stats', { params: { weeks } }),
  getUnpaidInvoicesByTxnWeek: (weekStart, weekEnd) => api.get('/quickbooks/unpaid-invoices-by-txn-week', { params: { weekStart, weekEnd } }),
  // Sync status
  getSyncStatus: () => api.get('/quickbooks/sync-status'),
  // Admin operations
  resetData: () => api.post('/quickbooks/reset'),
  
  // Customer notes
  getCustomerNotes: (customerId) => api.get(`/quickbooks/customers/${customerId}/notes`),
  addCustomerNote: (customerId, data) => api.post(`/quickbooks/customers/${customerId}/notes`, data),
  updateCustomerNote: (customerId, noteId, data) => api.put(`/quickbooks/customers/${customerId}/notes/${noteId}`, data),
  deleteCustomerNote: (customerId, noteId) => api.delete(`/quickbooks/customers/${customerId}/notes/${noteId}`),
  getCustomerNoteCounts: (customerIds) => api.post('/quickbooks/customers/note-counts', { customerIds }),
  getCustomerInvoiceNotes: (customerId) => api.get(`/quickbooks/customers/${customerId}/invoice-notes`),
  
  // Invoice notes
  getInvoiceNotes: (invoiceId) => api.get(`/quickbooks/invoices/${invoiceId}/notes`),
  addInvoiceNote: (invoiceId, data) => api.post(`/quickbooks/invoices/${invoiceId}/notes`, data),
  updateInvoiceNote: (invoiceId, noteId, data) => api.put(`/quickbooks/invoices/${invoiceId}/notes/${noteId}`, data),
  deleteInvoiceNote: (invoiceId, noteId) => api.delete(`/quickbooks/invoices/${invoiceId}/notes/${noteId}`),
  getInvoiceNoteCounts: (invoiceIds) => api.post('/quickbooks/invoices/note-counts', { invoiceIds }),
  
  // All invoice notes (User Notes tab)
  getAllInvoiceNotes: (params) => api.get('/quickbooks/all-invoice-notes', { params }),
  
  // Ship reference numbers
  uploadShipRefs: (data) => api.post('/quickbooks/upload-ship-refs', { data }),
  getShipRefs: (params) => api.get('/quickbooks/ship-refs', { params }),

  // Customer email addresses
  uploadCustomerEmails: (data) => api.post('/quickbooks/upload-customer-emails', { data }),

  // Follow-ups
  listFollowUps: (params) => api.get('/quickbooks/follow-ups', { params }),
  getFollowUpSummary: () => api.get('/quickbooks/follow-ups/summary'),
  getFollowUpCounts: (invoiceIds = [], customerIds = []) =>
    api.post('/quickbooks/follow-ups/counts', { invoiceIds, customerIds }),
  getFollowUpAssignableUsers: () => api.get('/quickbooks/follow-ups/assignable-users'),
  createFollowUp: (data) => api.post('/quickbooks/follow-ups', data),
  updateFollowUp: (followUpId, data) => api.put(`/quickbooks/follow-ups/${followUpId}`, data),
  completeFollowUp: (followUpId) => api.post(`/quickbooks/follow-ups/${followUpId}/complete`),
  snoozeFollowUp: (followUpId, hours) =>
    api.post(`/quickbooks/follow-ups/${followUpId}/snooze`, { hours }),
  deleteFollowUp: (followUpId) => api.delete(`/quickbooks/follow-ups/${followUpId}`),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

// Client error log review (admin). Params: limit, user, source, since.
export const clientLogsAPI = {
  list: (params) => api.get('/client-logs', { params }),
};

// Report Subscriptions API endpoints (scheduled reports admin)
export const reportSubscriptionAPI = {
  listAvailable: () => api.get('/report-subscriptions/available'),
  list: () => api.get('/report-subscriptions'),
  get: (id) => api.get(`/report-subscriptions/${id}`),
  create: (data) => api.post('/report-subscriptions', data),
  update: (id, data) => api.put(`/report-subscriptions/${id}`, data),
  remove: (id) => api.delete(`/report-subscriptions/${id}`),
  runNow: (id) => api.post(`/report-subscriptions/${id}/run-now`),
  listRuns: (id, params) => api.get(`/report-subscriptions/${id}/runs`, { params }),
};

// Inbound Email API endpoints (Phase 3)
export const inboundEmailAPI = {
  list: (params) => api.get('/inbound-emails', { params }),
  get: (id) => api.get(`/inbound-emails/${id}`),
  remove: (id) => api.delete(`/inbound-emails/${id}`),
  downloadAttachment: (id, idx) => api.get(`/inbound-emails/${id}/attachments/${idx}`, {
    responseType: 'blob'
  }),
  listDmarc: (params) => api.get('/inbound-emails/dmarc/reports', { params }),
  getDmarc: (id) => api.get(`/inbound-emails/dmarc/reports/${id}`),
};

// Driver Analytics API endpoints
export const driverAnalyticsAPI = {
  listReports: (type) => api.get('/driver-analytics/reports', { params: { type } }),
  getReport: (id) => api.get(`/driver-analytics/reports/${id}`),
  uploadReport: (type, file, label = '', onProgress) => {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('label', label);
    formData.append('file', file);
    return api.post('/driver-analytics/reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });
  },
  updateReport: (id, data) => api.put(`/driver-analytics/reports/${id}`, data),
  deleteReport: (id) => api.delete(`/driver-analytics/reports/${id}`),
  downloadReport: (id) => api.get(`/driver-analytics/reports/${id}/download`, {
    responseType: 'blob'
  })
};

// Driver Tablets API endpoints
export const driverTabletsAPI = {
  // Board
  getBoard: () => api.get('/driver-tablets/board'),
  getDiesel: () => api.get('/driver-tablets/diesel'),
  moveDriver: (driverId, zoneId, orderedDriverIds) =>
    api.put('/driver-tablets/board/move', { driverId, zoneId, orderedDriverIds }),
  reorderLoads: (orderedLoadIds) =>
    api.put('/driver-tablets/loads/order', { orderedLoadIds }),
  getActivity: () => api.get('/driver-tablets/activity'),
  getCalendar: (params) => api.get('/driver-tablets/calendar', { params }),
  getPreferences: () => api.get('/driver-tablets/preferences'),
  updatePreferences: (prefs) => api.put('/driver-tablets/preferences', prefs),
  getTabletStats: () => api.get('/driver-tablets/stats'),
  getUserReport: (params = { range: '7d' }) => api.get('/driver-tablets/reports/by-user', { params }),
  getUserReportLoads: (params) => api.get('/driver-tablets/reports/by-user/loads', { params }),
  getLoads: (params = {}) => api.get('/driver-tablets/loads', { params }),
  getUnbilled: (params = {}) => api.get('/driver-tablets/unbilled', { params }),
  getUnpaid: (params = {}) => api.get('/driver-tablets/unpaid', { params }),
  getUnpaidByDriver: (params = {}) => api.get('/driver-tablets/unpaid/by-driver', { params }),
  getLocations: () => api.get('/driver-tablets/locations'),
  getCustomers: () => api.get('/driver-tablets/customers'),

  // Live tracking (Motive)
  getTracking: () => api.get('/driver-tablets/tracking'),
  getDriverLocation: (driverId) => api.get(`/driver-tablets/drivers/${driverId}/location`),
  pollTracking: () => api.post('/driver-tablets/tracking/poll'),
  getTrackingAssets: () => api.get('/driver-tablets/tracking/assets'),
  updateTrackingAsset: (motiveVehicleId, data) =>
    api.put(`/driver-tablets/tracking/assets/${motiveVehicleId}`, data),

  // Dispatchers & zones
  getDispatchers: (includeInactive = false) =>
    api.get('/driver-tablets/dispatchers', { params: { includeInactive } }),
  createDispatcher: (data) => api.post('/driver-tablets/dispatchers', data),
  updateDispatcher: (id, data) => api.put(`/driver-tablets/dispatchers/${id}`, data),
  createZone: (data) => api.post('/driver-tablets/zones', data),
  updateZone: (id, data) => api.put(`/driver-tablets/zones/${id}`, data),

  // Drivers
  getDrivers: (params) => api.get('/driver-tablets/drivers', { params }),
  createDriver: (data) => api.post('/driver-tablets/drivers', data),
  getDriver: (id) => api.get(`/driver-tablets/drivers/${id}`),
  updateDriver: (id, data) => api.put(`/driver-tablets/drivers/${id}`, data),
  getDriverByEmployee: (employeeId) => api.get(`/driver-tablets/drivers/by-employee/${employeeId}`),
  getEmployeesLite: () => api.get('/driver-tablets/employees-lite'),
  getUnlinkedDrivers: (employeeId) =>
    api.get('/driver-tablets/drivers/unlinked', { params: { employeeId } }),
  toggleEmployeeDriver: (employeeId, enabled, linkDriverId = null) =>
    api.put('/driver-tablets/drivers/employee-toggle', { employeeId, enabled, linkDriverId }),
  getDriverShipments: (id, params) => api.get(`/driver-tablets/drivers/${id}/shipments`, { params }),
  exportDriverShipments: (id, params) =>
    api.get(`/driver-tablets/drivers/${id}/shipments/export`, { params }),
  getDriverNotes: (id) => api.get(`/driver-tablets/drivers/${id}/notes`),
  createDriverNote: (id, data) => api.post(`/driver-tablets/drivers/${id}/notes`, data),
  updateDriverNote: (noteId, data) => api.put(`/driver-tablets/notes/${noteId}`, data),
  deleteDriverNote: (noteId) => api.delete(`/driver-tablets/notes/${noteId}`),
  getTripEdit: (id) => api.get(`/driver-tablets/drivers/${id}/trip-edit`),
  getTripEditRevision: (id) => api.get(`/driver-tablets/drivers/${id}/trip-edit/revision`),
  createTrip: (driverId, data) => api.post(`/driver-tablets/drivers/${driverId}/trips`, data),
  updateTrip: (id, data) => api.put(`/driver-tablets/trips/${id}`, data),
  getMileage: (shipments) => api.post('/driver-tablets/mileage', { shipments }),
  recomputeTripMiles: (tripId) => api.post(`/driver-tablets/trips/${tripId}/recompute-miles`),
  getRouteForShipments: (shipments) => api.post('/driver-tablets/route', { shipments }),
  getTripRoute: (tripId) => api.get(`/driver-tablets/trips/${tripId}/route`),

  // Shipments (loads)
  createShipment: (data) => api.post('/driver-tablets/shipments', data),
  updateShipment: (id, data) => api.put(`/driver-tablets/shipments/${id}`, data),
  undoShipmentFlag: (id, field) => api.post(`/driver-tablets/shipments/${id}/undo-flag`, { field }),
  getSettlements: (params) => api.get('/driver-tablets/settlements', { params }),

  // Pay sheets (paySheets tab permission)
  getPaySheetConfig: () => api.get('/driver-tablets/pay-sheets/config'),
  updatePaySheetConfig: (data) => api.put('/driver-tablets/pay-sheets/config', data),
  getPaySheetUnpaidByDriver: () => api.get('/driver-tablets/pay-sheets/unpaid-by-driver'),
  getPaySheetUnpaidLoads: (driverId) => api.get(`/driver-tablets/pay-sheets/driver/${driverId}/unpaid-loads`),
  getPaySheetDriverLoads: (driverId, limit) =>
    api.get(`/driver-tablets/pay-sheets/driver/${driverId}/loads`, { params: { limit } }),
  getPaySheetHistory: (driverId, before) =>
    api.get(`/driver-tablets/pay-sheets/driver/${driverId}/history`, { params: { before } }),
  getPaySheets: (weekStart) => api.get('/driver-tablets/pay-sheets', { params: { weekStart } }),
  createPaySheet: (data) => api.post('/driver-tablets/pay-sheets', data),
  updatePaySheet: (id, data) => api.put(`/driver-tablets/pay-sheets/${id}`, data),
  finalizePaySheet: (id) => api.post(`/driver-tablets/pay-sheets/${id}/finalize`),
  reopenPaySheet: (id) => api.post(`/driver-tablets/pay-sheets/${id}/reopen`),
  deletePaySheet: (id) => api.delete(`/driver-tablets/pay-sheets/${id}`),
  assignShipment: (id, data) => api.put(`/driver-tablets/shipments/${id}/assign`, data),
  deleteShipment: (id) => api.delete(`/driver-tablets/shipments/${id}`),
  saveUserNote: (id, text) => api.put(`/driver-tablets/shipments/${id}/user-note`, { text }),

  // Legacy sync (admin)
  getSyncStatus: () => api.get('/driver-tablets/sync/status'),
  runSync: (mode = 'incremental') => api.post('/driver-tablets/sync/run', { mode }),
  pauseSync: (paused) => api.post('/driver-tablets/sync/pause', { paused }),
  closeOrphanTrips: () => api.post('/driver-tablets/sync/close-orphan-trips'),
};

// PC*Miler mileage & directions (Driver Tablets). Backend proxies to the LAN API.
export const pcmilerAPI = {
  getHealth: () => api.get('/pcmiler/health'),
  lookup: (q) => api.get('/pcmiler/lookup', { params: { q } }),
  locate: (q) => api.get('/pcmiler/locate', { params: { q } }),
  route: (data) => api.post('/pcmiler/route', data),
  getMapLine: (stops) => api.post('/pcmiler/map', { stops }),
  // Guest links (pcmilerShare tablets permission)
  listShares: () => api.get('/pcmiler/shares'),
  createShare: (data) => api.post('/pcmiler/shares', data),
  revokeShare: (id) => api.delete(`/pcmiler/shares/${id}`)
};

// Shared PC Miler guest links (/miler/link/<token>): no sign-in, so a plain
// client with no auth headers or session refresh. Same call shape as
// pcmilerAPI (minus the map fallback and anything trucks/portal related).
const milerLinkApi = axios.create({
  baseURL: `${BASE_URL}/api/pcmiler-link`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

export const pcmilerLinkAPI = (token) => {
  const base = `/${encodeURIComponent(token)}`;
  return {
    info: () => milerLinkApi.get(base),
    getHealth: () => milerLinkApi.get(`${base}/health`),
    lookup: (q) => milerLinkApi.get(`${base}/lookup`, { params: { q } }),
    locate: (q) => milerLinkApi.get(`${base}/locate`, { params: { q } }),
    route: (data) => milerLinkApi.post(`${base}/route`, data),
    getMapLine: null
  };
};

// Net2 door unlock (doors tablet sub-permission). Backend proxies to the LAN API.
export const doorsAPI = {
  getHealth: () => api.get('/doors/health'),
  getActivity: () => api.get('/doors/activity'),
  openDoor: (name) => api.post(`/doors/${encodeURIComponent(name)}/open`),
  getShares: () => api.get('/doors/shares'),
  createShare: (data) => api.post('/doors/shares', data),
  revokeShare: (id) => api.delete(`/doors/shares/${id}`)
};

// Public guest unlock page — bare client so 404s don't toast / trigger login refresh.
export const doorLinkAPI = {
  getShare: (token) => bareApi.get(`/door-links/${token}`),
  openDoor: (token, door) => bareApi.post(`/door-links/${token}/open`, { door })
};

// Driver Payroll: load sheet + pay stub batches emailed to drivers (driverPayroll
// tab permission). Every file a batch touches is stored server-side.
export const driverPayrollAPI = {
  // Read-only: active Lanita Specialized LLC employees, department Truck Driver
  getDrivers: () => api.get('/driver-payroll/drivers'),

  getBatches: (params) => api.get('/driver-payroll/batches', { params }),
  getBatch: (id) => api.get(`/driver-payroll/batches/${id}`),
  createBatch: (formData) => api.post('/driver-payroll/batches', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000
  }),
  updateBatch: (id, data) => api.put(`/driver-payroll/batches/${id}`, data),
  deleteBatch: (id) => api.delete(`/driver-payroll/batches/${id}`),
  getSourceFile: (id, which) =>
    api.get(`/driver-payroll/batches/${id}/files/${which}`, { responseType: 'blob', timeout: 120000 }),
  getDocumentFile: (id, docId) =>
    api.get(`/driver-payroll/batches/${id}/documents/${docId}/file`, { responseType: 'blob' }),
  getZip: (id) => api.get(`/driver-payroll/batches/${id}/zip`, { responseType: 'blob', timeout: 180000 }),
  addDocument: (id, data) => api.post(`/driver-payroll/batches/${id}/documents`, data),
  updateDocument: (id, docId, data) => api.put(`/driver-payroll/batches/${id}/documents/${docId}`, data),
  deleteDocument: (id, docId) => api.delete(`/driver-payroll/batches/${id}/documents/${docId}`),
  sendDocument: (id, docId) =>
    // {} not null: axios serializes null to the literal body "null", which
    // express.json() (strict) rejects with a 400 before the route runs
    api.post(`/driver-payroll/batches/${id}/documents/${docId}/send`, {}, { timeout: 60000 }),
};

// Driver credential documents (License/Medical/TWIC) viewable from the tablet
// pages, gated by the standalone "Driver Documents" permission.
export const driverDocumentsAPI = {
  list: (driverId) => api.get(`/driver-documents/driver/${driverId}`),
  getFile: (driverId, documentId, download = false) =>
    api.get(`/driver-documents/driver/${driverId}/${documentId}/file`, {
      params: download ? { download: 1 } : {},
      responseType: 'blob'
    }),
};

// Motive driver-uploaded documents (Proof of Delivery), synced locally.
export const driverPODAPI = {
  list: (params = {}) => api.get('/driver-pod', { params }),
  getDocument: (id) => api.get(`/driver-pod/${id}`),
  getCounts: () => api.get('/driver-pod/counts'),
  setArchived: (id, archived) => api.put(`/driver-pod/${id}/archive`, { archived }),
  saveCopy: (id) => api.post(`/driver-pod/${id}/save-copy`),
  getFile: (id, download = false, page = 1, contrast = 100) =>
    api.get(`/driver-pod/${id}/file`, {
      params: {
        ...(download ? { download: 1 } : {}),
        page,
        ...(contrast !== 100 ? { contrast } : {})
      },
      responseType: 'blob'
    }),
  downloadPdf: (id, contrast = 100) =>
    api.get(`/driver-pod/${id}/pdf`, {
      params: contrast !== 100 ? { contrast } : {},
      responseType: 'blob'
    }),
  getSyncStatus: () => api.get('/driver-pod/sync/status'),
  runSync: () => api.post('/driver-pod/sync/run'),
  deleteDocument: (id) => api.delete(`/driver-pod/${id}`),
};

// DocSign API endpoints
export const docsignAPI = {
  // Get employees for signer selection
  getEmployees: () => api.get('/docsign/employees'),
  
  // Document management (authenticated)
  getDocuments: (params) => api.get('/docsign', { params }),
  getDocument: (documentId) => api.get(`/docsign/${documentId}`),
  createDocument: (formData) => {
    return api.post('/docsign', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  sendEmails: (documentId, signerIds) => api.post(`/docsign/${documentId}/send`, { signerIds }),
  deleteDocument: (documentId) => api.delete(`/docsign/${documentId}`),
  downloadDocument: (documentId) => api.get(`/docsign/${documentId}/download`, {
    responseType: 'blob'
  }),
  downloadSignedDocument: (signatureId) => api.get(`/docsign/signatures/${signatureId}/download`, {
    responseType: 'blob'
  }),
  
  // Signer management
  addSigners: (documentId, signers) => api.post(`/docsign/${documentId}/signers`, { signers }),
  removeSigner: (documentId, signerId) => api.delete(`/docsign/${documentId}/signers/${signerId}`),
  
  // Notification recipients management
  updateNotifications: (documentId, notificationEmails) => api.put(`/docsign/${documentId}/notifications`, { notificationEmails }),
  
  // Public signing routes (no auth required - uses token)
  getDocumentForSigning: (token) => api.get(`/docsign/sign/${token}`),
  getDocumentContent: (token) => api.get(`/docsign/sign/${token}/document`, {
    responseType: 'blob'
  }),
  submitSignature: (token, data) => api.post(`/docsign/sign/${token}`, data),
};

// --- Public form applications ------------------------------------------------
// Applicant side uses its own axios instance: applicants carry an applicant
// token (localStorage), never the portal token, and must not trigger the
// portal 401-redirect interceptors.
const applicantApi = axios.create({
  baseURL: `${BASE_URL}/api/apply`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

applicantApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('applicantToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const applyAPI = {
  // Public
  getInfo: (slug) => applicantApi.get(`/info/${slug}`),
  requestCode: (email, slug, website) => applicantApi.post('/request-code', { email, slug, website }),
  verifyCode: (email, code) => applicantApi.post('/verify-code', { email, code }),
  // Applicant-authenticated
  getForm: (slug) => applicantApi.get(`/form/${slug}`),
  saveDraft: (slug, data) => applicantApi.put(`/draft/${slug}`, { data }),
  submit: (slug, payload) => applicantApi.post(`/submit/${slug}`, payload),
  uploadDocument: (slug, slotKey, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return applicantApi.post(`/documents/${slug}`, formData, {
      params: { slot: slotKey },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },
  getDocument: (slug, docId) => applicantApi.get(`/documents/${slug}/${docId}`, { responseType: 'blob' }),
  deleteDocument: (slug, docId) => applicantApi.delete(`/documents/${slug}/${docId}`),
};

// --- Driver upload portal (/drivers) -----------------------------------------
// Drivers carry a driver token (localStorage), never the portal token, and
// must not trigger the portal 401-redirect interceptors. The backend slides
// the session: active drivers get a fresh token in the X-Driver-Token header.
const DRIVER_TOKEN_KEY = 'driverPortalToken';

const driverPortalApi = axios.create({
  baseURL: `${BASE_URL}/api/driver-portal`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

driverPortalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(DRIVER_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

driverPortalApi.interceptors.response.use((response) => {
  const refreshed = response.headers['x-driver-token'];
  if (refreshed) {
    localStorage.setItem(DRIVER_TOKEN_KEY, refreshed);
  }
  return response;
});

export const driverPortalAPI = {
  tokenKey: DRIVER_TOKEN_KEY,
  // Public
  requestCode: (email, website) => driverPortalApi.post('/request-code', { email, website }),
  verifyCode: (email, code) => driverPortalApi.post('/verify-code', { email, code }),
  // Driver-authenticated
  me: () => driverPortalApi.get('/me'),
  createUpload: (data) => driverPortalApi.post('/uploads', data || {}),
  uploadPage: (uploadId, blob, onProgress) => {
    const formData = new FormData();
    formData.append('file', blob, 'page.jpg');
    return driverPortalApi.post(`/uploads/${uploadId}/pages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },
  deletePage: (uploadId, pageId) => driverPortalApi.delete(`/uploads/${uploadId}/pages/${pageId}`),
  deleteUpload: (uploadId) => driverPortalApi.delete(`/uploads/${uploadId}`),
  submitUpload: (uploadId, data) => driverPortalApi.post(`/uploads/${uploadId}/submit`, data || {}),
  getPage: (uploadId, pageId) => driverPortalApi.get(`/uploads/${uploadId}/pages/${pageId}`, { responseType: 'blob' }),
  getPdf: (uploadId) => driverPortalApi.get(`/uploads/${uploadId}/pdf`, { responseType: 'blob' }),
  // Driver docs the office posts (read-only)
  docs: () => driverPortalApi.get('/docs'),
  getDoc: (id) => driverPortalApi.get(`/docs/${id}/file`, { responseType: 'blob', timeout: 120000 }),
};

// Staff side of the driver upload portal (portal-authenticated)
export const podUploadsAPI = {
  getUploads: (params) => api.get('/driver-portal/staff/uploads', { params }),
  getPdf: (id) => api.get(`/driver-portal/staff/uploads/${id}/pdf`, { responseType: 'blob' }),
  getPage: (id, pageId) => api.get(`/driver-portal/staff/uploads/${id}/pages/${pageId}`, { responseType: 'blob' }),
  deleteUpload: (id) => api.delete(`/driver-portal/staff/uploads/${id}`),
  getConfig: () => api.get('/driver-portal/staff/config'),
  // config = { notificationEmails?, tripNotificationEmails? } — only the
  // provided lists are updated
  updateConfig: (config) => api.put('/driver-portal/staff/config', config),
};

// Staff side (portal-authenticated)
export const applicationsAPI = {
  getTemplates: () => api.get('/applications/templates'),
  updateTemplate: (slug, updates) => api.put(`/applications/templates/${slug}`, updates),
  createTemplate: (data) => api.post('/applications/templates', data),
  getSubmissions: (params) => api.get('/applications', { params }),
  getSubmission: (id) => api.get(`/applications/${id}`),
  downloadPdf: (id) => api.get(`/applications/${id}/pdf`, { responseType: 'blob' }),
  getDocument: (id, docId) => api.get(`/applications/${id}/documents/${docId}`, { responseType: 'blob' }),
  updateStatus: (id, status) => api.put(`/applications/${id}/status`, { status }),
  addNote: (id, text) => api.post(`/applications/${id}/notes`, { text }),
  deleteSubmission: (id) => api.delete(`/applications/${id}`),
};

// Water Usage page (/water-usage). Every write returns the full recomputed
// page payload, same shape as get().
export const waterAPI = {
  get: () => api.get('/water'),
  addReading: (data) => api.post('/water/readings', data),
  updateReading: (id, data) => api.put(`/water/readings/${id}`, data),
  deleteReading: (id) => api.delete(`/water/readings/${id}`),
  addSwap: (data) => api.post('/water/swaps', data),
  updateSwap: (swapId, data) => api.put(`/water/swaps/${swapId}`, data),
  deleteSwap: (swapId) => api.delete(`/water/swaps/${swapId}`),
  updateMeter: (id, data) => api.put(`/water/meters/${id}`, data),
};

// Fuel page (/fuel): FleetOne fuel card purchases from forwarded statements
// and activity reports.
export const fuelAPI = {
  transactions: (params) => api.get('/fuel/transactions', { params }),
  transaction: (id) => api.get(`/fuel/transactions/${id}`),
  // Count a purchase under another unit / driver ({ unit, driver, note }; '' = as printed)
  setAssignment: (id, data) => api.put(`/fuel/transactions/${id}/assignment`, data),
  reports: () => api.get('/fuel/reports'),
  // Pilot direct bill by week ({ weeks }), and one week's lines ({ account, start })
  directBill: (params) => api.get('/fuel/direct-bill', { params }),
  directBillWeek: (params) => api.get('/fuel/direct-bill/week', { params }),
  reportFile: (id) => api.get(`/fuel/reports/${id}/file`, { responseType: 'blob', timeout: 120000 }),
  upload: (formData) => api.post('/fuel/reports/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000
  }),
  reparse: (id) => api.post(`/fuel/reports/${id}/reparse`),
  removeReport: (id) => api.delete(`/fuel/reports/${id}`),
};

// Insurance page (/insurance). Every write returns the full policy list, same
// shape as get(): { policies, canManage }.
export const insuranceAPI = {
  get: () => api.get('/insurance'),
  create: (data) => api.post('/insurance', data),
  update: (id, data) => api.put(`/insurance/${id}`, data),
  remove: (id) => api.delete(`/insurance/${id}`),
  uploadDocument: (id, formData) => api.post(`/insurance/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000
  }),
  updateDocument: (id, docId, data) => api.put(`/insurance/${id}/documents/${docId}`, data),
  removeDocument: (id, docId) => api.delete(`/insurance/${id}/documents/${docId}`),
  getDocumentFile: (id, docId) => api.get(`/insurance/${id}/documents/${docId}/file`, { responseType: 'blob', timeout: 120000 }),
};

// Dropbox page (/dropbox). space: 'personal' (My files) | 'company'.
// Uploads go in chunks: startUpload → putChunk (each ≤ chunkBytes, at the
// byte offset received so far) → finishUpload; see pages/dropbox/dropboxUploads.js.
export const dropboxAPI = {
  list: (space, folder) => api.get('/dropbox', { params: { space, folder: folder || undefined } }),
  search: (space, q) => api.get('/dropbox/search', { params: { space, q } }),
  folders: () => api.get('/dropbox/folders'),
  createFolder: (data) => api.post('/dropbox/folders', data),
  update: (id, data) => api.patch(`/dropbox/items/${id}`, data),
  remove: (id) => api.delete(`/dropbox/items/${id}`),
  getFile: (id) => api.get(`/dropbox/items/${id}/file`, { responseType: 'blob', timeout: 300000 }),
  downloadUrl: (id) => api.post(`/dropbox/items/${id}/download`),
  startUpload: (data) => api.post('/dropbox/uploads', data),
  putChunk: (uploadId, offset, blob, config = {}) => api.put(`/dropbox/uploads/${uploadId}`, blob, {
    ...config,
    params: { offset },
    headers: { 'Content-Type': 'application/octet-stream' },
    timeout: 300000
  }),
  finishUpload: (uploadId) => api.post(`/dropbox/uploads/${uploadId}/complete`, {}, { timeout: 120000 }),
  cancelUpload: (uploadId) => api.delete(`/dropbox/uploads/${uploadId}`),
  links: () => api.get('/dropbox/links'),
  itemLinks: (id) => api.get(`/dropbox/items/${id}/links`),
  createLink: (id, data) => api.post(`/dropbox/items/${id}/links`, data),
  revokeLink: (linkId) => api.delete(`/dropbox/links/${linkId}`),
};

// Driver docs (/driver-docs): files drivers see under "Driver docs" on
// pod.lanita.us. One file per upload request (15 MB cap).
export const portalDocsAPI = {
  list: () => api.get('/portal-docs'),
  createFolder: (data) => api.post('/portal-docs/folders', data),
  upload: (file, { parent, name } = {}, onProgress) => {
    const formData = new FormData();
    formData.append('name', name || file.name);
    if (parent) formData.append('parent', parent);
    formData.append('file', file);
    return api.post('/portal-docs/files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },
  replace: (id, file) => {
    const formData = new FormData();
    formData.append('name', file.name);
    formData.append('file', file);
    return api.put(`/portal-docs/items/${id}/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  update: (id, data) => api.patch(`/portal-docs/items/${id}`, data),
  // Several selected items at once
  bulkMove: (ids, parent) => api.post('/portal-docs/bulk/move', { ids, parent: parent || null }),
  bulkExpiry: (ids, expiresAt) => api.post('/portal-docs/bulk/expiry', { ids, expiresAt }),
  bulkDelete: (ids) => api.post('/portal-docs/bulk/delete', { ids }),
  remove: (id) => api.delete(`/portal-docs/items/${id}`),
  getFile: (id) => api.get(`/portal-docs/items/${id}/file`, { responseType: 'blob', timeout: 120000 }),
};

// Public Dropbox share page (/f/:token): no sign-in. On a link with a
// download limit, open() uses one download for this visit and returns
// { pass, downloadsLeft }; the pass lets file() and the download run for the
// rest of the visit without using more.
export const dropboxShareAPI = {
  info: (token) => bareApi.get(`/dropbox-public/links/${token}`),
  open: (token) => bareApi.post(`/dropbox-public/links/${token}/open`, {}),
  // The bytes behind the preview and viewer (PDFs and images)
  file: (token, pass) => bareApi.get(`/dropbox-public/links/${token}/file`, {
    params: { pass: pass || undefined },
    responseType: 'blob',
    timeout: 300000
  }),
  downloadHref: (token, pass) => `${BASE_URL}/api/dropbox-public/links/${token}/download${pass ? `?pass=${encodeURIComponent(pass)}` : ''}`,
};

// Cameras page (/cameras). Video runs over the relay WebSocket
// (/api/cameras/ws, see pages/cameras/cameraConnection.js); this is the list.
export const camerasAPI = {
  get: () => api.get('/cameras'),
  // Recorded spans for part of an NVR-local day (seconds), see routes/cameras.js.
  recordings: (params) => api.get('/cameras/recordings', { params, timeout: 60000 }),
  // This user's grid: { order: [cameraId], hidden: [cameraId] }.
  saveLayout: (layout) => api.put('/cameras/layout', { order: layout.order, hidden: layout.hidden }),
};

// Traffic page: HTTPS proxy request stats. params: { from, to, tz, site }
// (from/to ISO instants); top() adds kind = blocked | error | page | agent.
// visitors(): the router's incoming connections; params { from, to, tz,
// country (ISO code), service (web | restreamer | viniplay | plex | vpn | other) }.
export const trafficAPI = {
  summary: (params) => api.get('/traffic/summary', { params }),
  top: (params) => api.get('/traffic/top', { params }),
  visitors: (params) => api.get('/traffic/visitors', { params }),
};

// Driver Tablets → Ramps (equipment check-out/in). Writes return the full
// list, same shape as get(): { items, drivers, owners, types, pastDue }.
export const equipmentAPI = {
  get: () => api.get('/driver-tablets/equipment'),
  summary: () => api.get('/driver-tablets/equipment/summary'),
  heldBy: (driverId) => api.get(`/driver-tablets/equipment/held/${driverId}`),
  history: (id) => api.get(`/driver-tablets/equipment/${id}/history`),
  create: (data) => api.post('/driver-tablets/equipment', data),
  update: (id, data) => api.put(`/driver-tablets/equipment/${id}`, data),
  setRetired: (id, retired) => api.put(`/driver-tablets/equipment/${id}/retired`, { retired }),
  remove: (id) => api.delete(`/driver-tablets/equipment/${id}`),
  checkout: (id, data) => api.post(`/driver-tablets/equipment/${id}/checkout`, data),
  checkin: (id, data) => api.post(`/driver-tablets/equipment/${id}/checkin`, data),
  updateCheckout: (id, checkoutId, data) => api.put(`/driver-tablets/equipment/${id}/checkouts/${checkoutId}`, data),
  removeCheckout: (id, checkoutId) => api.delete(`/driver-tablets/equipment/${id}/checkouts/${checkoutId}`),
  uploadDocument: (id, formData) => api.post(`/driver-tablets/equipment/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000
  }),
  removeDocument: (id, docId) => api.delete(`/driver-tablets/equipment/${id}/documents/${docId}`),
  getDocumentFile: (id, docId) => api.get(`/driver-tablets/equipment/${id}/documents/${docId}/file`, { responseType: 'blob', timeout: 120000 }),
};

// Browser plugins page (/plugins)
export const pluginsAPI = {
  list: () => api.get('/plugins'),
  download: (plugin) => downloadFile(`/plugins/${plugin.id}/download`, plugin.zipName),
};

// File download helper
export const downloadFile = async (url, filename) => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    });
    
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    toast.error('Failed to download file');
    throw error;
  }
};

// Upload progress helper
export const uploadWithProgress = (url, data, onProgress) => {
  return api.post(url, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });
};

export default api; 